import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { doc, getDoc, collection, addDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/firebase';
import ClientLayout from '@/components/layout/ClientLayout';

interface UserProfile {
  name: string;
  email: string;
  phoneNumber: string;
  organization: string;
}

interface ParkingApplication {
  id?: string;
  uid: string;
  name: string;
  email: string;
  phoneNumber: string;
  organization: string;
  applicationType: 'chassis' | 'individual';
  period: 1 | 2 | 3;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  termsAgreed: boolean;
  additionalRequest?: string;
  periodId?: string; // 신청 차수 ID
}

interface ParkingPeriod {
  id: string;
  sequence: number;
  applicationStartDate: any;
  applicationEndDate: any;
  lotteryDate: any;
  usageStartDate: any;
  isActive: boolean;
  createdAt: any;
  updatedAt: any;
}

const ParkingApplicationPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = getAuth();
  const user = auth.currentUser;

  // 날짜 포맷팅 헬퍼 함수
  const formatDate = (date: any): string => {
    if (!date) return '날짜 정보 없음';
    
    try {
      let targetDate: Date | null = null;

      // Firestore Timestamp 객체인 경우 (toDate 메서드 있음)
      if (date && typeof date === 'object' && typeof date.toDate === 'function') {
        targetDate = date.toDate();
      }
      // Date 객체인 경우
      else if (date instanceof Date) {
        targetDate = date;
      }
      // 숫자인 경우 (timestamp in milliseconds)
      else if (typeof date === 'number') {
        targetDate = new Date(date);
      }
      // 문자열인 경우
      else if (typeof date === 'string') {
        targetDate = new Date(date);
      }
      // seconds와 nanoseconds를 가진 객체인 경우 (Firestore Timestamp)
      else if (date && typeof date === 'object' && 'seconds' in date) {
        targetDate = new Date(date.seconds * 1000);
      }
      // _seconds를 가진 객체인 경우
      else if (date && typeof date === 'object' && '_seconds' in date) {
        targetDate = new Date(date._seconds * 1000);
      }

      if (targetDate && !isNaN(targetDate.getTime())) {
        return targetDate.toLocaleDateString('ko-KR');
      }

      return '날짜 정보 없음';
    } catch (error) {
      return '날짜 정보 없음';
    }
  };
  
  // URL state로부터 수정 모드, 기존 신청 정보, 현재 신청 차수 정보 가져오기
  const { editMode = false, application: existingApplication, currentPeriod } = location.state || {};

  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [formData, setFormData] = useState<Partial<ParkingApplication>>({
    applicationType: 'individual',
    period: 1,
    termsAgreed: false,
    additionalRequest: ''
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/signin');
      return;
    }



    const fetchUserProfile = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'user_profiles', user.uid));
        if (userDoc.exists()) {
          const profile = userDoc.data() as UserProfile;
          setUserProfile(profile);
          
          // 수정 모드인 경우 기존 신청 정보로 폼 데이터 설정
          if (editMode && existingApplication) {
            setFormData({
              applicationType: existingApplication.applicationType,
              period: existingApplication.period,
              termsAgreed: existingApplication.termsAgreed,
              additionalRequest: existingApplication.additionalRequest || ''
            });
          } else {
            // 새 신청인 경우 사용자 프로필 정보만 설정
            setFormData(prev => ({
              ...prev,
              name: profile.name,
              email: profile.email,
              phoneNumber: profile.phoneNumber,
              organization: profile.organization
            }));
          }
        }
      } catch (error) {
        console.error('사용자 프로필 조회 중 오류:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [user, navigate, editMode, existingApplication]);

  const handleInputChange = (field: keyof ParkingApplication, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user || !userProfile) return;
    
    if (!formData.termsAgreed) {
      alert('이용약관에 동의해 주세요.');
      return;
    }

    // 신규 신청인 경우 현재 신청 차수가 있는지 확인
    if (!editMode && !currentPeriod) {
      alert('현재 활성화된 신청 차수가 없습니다.');
      return;
    }

    setSubmitting(true);

    try {
      const applicationData = {
        uid: user.uid,
        name: userProfile.name,
        email: userProfile.email,
        phoneNumber: userProfile.phoneNumber,
        organization: userProfile.organization,
        applicationType: formData.applicationType!,
        period: formData.period!,
        termsAgreed: formData.termsAgreed,
        additionalRequest: formData.additionalRequest || '',
        updatedAt: serverTimestamp(),
        ...(currentPeriod && { periodId: currentPeriod.id }) // 신청 차수 ID 포함
      };

      if (editMode && existingApplication?.id) {
        // 기존 신청 수정
        await updateDoc(doc(db, 'parking_applications', existingApplication.id), applicationData);
        alert('신청 내용이 수정되었습니다.');
      } else {
        // 새 신청 생성
        await addDoc(collection(db, 'parking_applications'), {
          ...applicationData,
          status: 'pending',
          createdAt: serverTimestamp()
        });
        alert('신청이 완료되었습니다.');
      }

      navigate('/parking');
    } catch (error) {
      console.error('신청 처리 중 오류:', error);
      alert('신청 처리 중 오류가 발생했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <ClientLayout>
        <div className="flex justify-center items-center min-h-[50vh]">
          <div className="text-lg text-gray-600">로딩 중...</div>
        </div>
      </ClientLayout>
    );
  }

  if (!userProfile) {
    return (
      <ClientLayout>
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="text-center py-12">
              <p className="text-red-600 mb-4">사용자 프로필을 찾을 수 없습니다.</p>
              <button
                onClick={() => navigate('/parking')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                돌아가기
              </button>
            </div>
          </div>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="w-full min-h-screen bg-white">
        {/* 본문 컨테이너 - 중앙 정렬 */}
        <div className="w-[760px] mx-auto relative pt-8">
          {/* 헤더 섹션 */}
          <div className="mt-8">
            <div className="flex items-center justify-between">
              <div className="text-slate-800 text-4xl font-extrabold font-['Pretendard'] leading-9">
                {editMode ? '신청 내용 수정' : '웅동 임시 화물차 주차장 정기권 신청'}
              </div>
              {currentPeriod && (
                <div className="text-right">
                  <div className="text-[#1e2c49] text-lg font-semibold">
                    {currentPeriod.sequence}차 신청
                  </div>
                  <div className="text-[#6b7684] text-sm space-y-1">
                    <div>신청 기간: {formatDate(currentPeriod.applicationStartDate)} ~ {formatDate(currentPeriod.applicationEndDate)}</div>
                    <div>추첨일: {formatDate(currentPeriod.lotteryDate)}</div>
                    <div>이용 시작일: {formatDate(currentPeriod.usageStartDate)}</div>
                  </div>
                </div>
              )}
            </div>
            <div className="w-full h-px mt-8 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>
            <div className="mt-8 flex items-center justify-between">
              <div className="text-[#1e2c49] text-2xl font-bold font-['Pretendard'] leading-9">
                {editMode ? '신청 정보 수정' : '신청서 작성'}
              </div>
            </div>
          </div>

          {/* 구분선 */}
          <div className="w-full h-px mt-8 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* 신청자 정보 (읽기 전용) */}
            <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
              <div className="p-8">
                <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-4">
                  신청자 정보
                </h2>
                <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-8"></div>
                <div className="grid grid-cols-2 gap-8">
                  <div>
                    <p className="text-[#6b7684] text-lg font-medium mb-2">성명</p>
                    <p className="text-[#1e2c49] text-[19px] font-semibold">{userProfile.name}</p>
                  </div>
                  <div>
                    <p className="text-[#6b7684] text-lg font-medium mb-2">소속</p>
                    <p className="text-[#1e2c49] text-[19px] font-semibold">{userProfile.organization}</p>
                  </div>
                  <div>
                    <p className="text-[#6b7684] text-lg font-medium mb-2">이메일</p>
                    <p className="text-[#1e2c49] text-[19px] font-semibold">{userProfile.email}</p>
                  </div>
                  <div>
                    <p className="text-[#6b7684] text-lg font-medium mb-2">연락처</p>
                    <p className="text-[#1e2c49] text-[19px] font-semibold">{userProfile.phoneNumber}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* 신청 유형 - 숨김 처리 (개별화물차 신청 고정) */}
            {/* 신청 유형은 'individual'로 고정되며, DB에 저장되지만 사용자에게는 표시되지 않음 */}

            {/* 신청 기간 선택 */}
            <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
              <div className="p-8">
                <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-4">
                  신청 기간
                </h2>
                <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-8"></div>
                <div className="grid grid-cols-3 gap-6">
                  {[1, 2, 3].map(period => (
                    <div
                      key={period}
                      className={`p-6 border-2 rounded-sm cursor-pointer transition-colors ${
                        formData.period === period
                          ? 'border-[#155acb] bg-[#f2f6fb]'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                      onClick={() => handleInputChange('period', period)}
                    >
                      <div className="text-center">
                        <div className={`text-3xl font-bold mb-2 ${
                          formData.period === period ? 'text-[#155acb]' : 'text-[#1e2c49]'
                        }`}>
                          {period}개월
                        </div>
                        <div className="text-[#6b7684] text-base">
                          {period === 1}
                          {period === 2}
                          {period === 3}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* 추가 요청사항 */}
            <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
              <div className="p-8">
                <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-4">
                  추가 요청사항
                </h2>
                <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-8"></div>
                <textarea
                  value={formData.additionalRequest}
                  onChange={(e) => handleInputChange('additionalRequest', e.target.value)}
                  placeholder="추가로 전달하고 싶은 내용이 있으시면 입력해 주세요."
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#155acb] focus:border-transparent text-[#1e2c49] text-base resize-none"
                />
              </div>
            </div>

            {/* 이용약관 동의 */}
            <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
              <div className="p-8">
                <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-4">
                  이용약관 동의
                </h2>
                <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-8"></div>
                <div className="border border-gray-300 rounded-sm p-6 bg-gray-50 mb-6">
                  <div className="max-h-40 overflow-y-auto text-[#6b7684] text-base space-y-3">
                    <h3 className="text-[#1e2c49] text-lg font-semibold">주차장 정기이용 약관</h3>
                    <p>제1조 (목적) 본 약관은 부산항신항시설관리센터 주차장의 정기이용에 관한 조건 및 절차를 정함을 목적으로 합니다.</p>
                    <p>제2조 (이용기간) 정기이용 기간은 1개월, 2개월, 3개월 중 선택하여 신청할 수 있습니다.</p>
                    <p>제3조 (신청절차) 신청서 제출 후 관리자 검토를 거쳐 추첨을 통해 이용자를 선정합니다.</p>
                    <p>제4조 (이용료) 이용료는 별도 고지되는 요금표에 따라 납부해야 합니다.</p>
                    <p>제5조 (준수사항) 이용자는 주차장 이용규칙을 준수해야 하며, 위반 시 이용이 제한될 수 있습니다.</p>
                    <p>제6조 (책임) 차량 도난, 파손 등에 대한 책임은 이용자 본인에게 있습니다.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowTerms(true)}
                    className="mt-4 text-[#155acb] hover:text-[#155acb]/80 text-base underline"
                  >
                    전체 약관 보기
                  </button>
                </div>
                <div className="flex items-start gap-4">
                  <input
                    id="terms"
                    type="checkbox"
                    checked={formData.termsAgreed}
                    onChange={(e) => handleInputChange('termsAgreed', e.target.checked)}
                    className="h-5 w-5 text-[#155acb] focus:ring-[#155acb] border-gray-300 rounded mt-1"
                  />
                  <label htmlFor="terms" className="text-[#1e2c49] text-base font-medium">
                    상기 이용약관을 확인하였으며, 모든 내용에 동의합니다. <span className="text-red-500">*</span>
                  </label>
                </div>
              </div>
            </div>

            {/* 하단 여백 - 고정 버튼을 위한 공간 확보 */}
            <div className="h-32"></div>

            {/* 제출 버튼 */}
            <div className="fixed bottom-0 left-0 w-full h-24 bg-white border-t border-gray-200">
              <div className="max-w-[1600px] mx-auto px-4 h-full flex items-center justify-end gap-4">
                <button
                  type="button"
                  onClick={() => navigate('/parking')}
                  className="w-44 h-16 rounded-sm border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <div className="text-center text-gray-700 text-lg font-bold font-['Pretendard'] leading-9">
                    취소
                  </div>
                </button>
                <button
                  type="submit"
                  disabled={submitting || !formData.termsAgreed}
                  className={`w-44 h-16 rounded-sm transition-colors ${
                    submitting || !formData.termsAgreed
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-[#155acb] hover:bg-[#155acb]/90'
                  }`}
                >
                  <div className="text-center text-white text-lg font-bold font-['Pretendard'] leading-9">
                    {submitting ? '처리 중...' : (editMode ? '수정 완료' : '신청하기')}
                  </div>
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* 약관 전체보기 모달 */}
      {showTerms && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">주차장 정기이용 약관</h3>
                <button
                  onClick={() => setShowTerms(false)}
                  className="text-gray-600 hover:text-gray-800"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="text-sm text-gray-700 space-y-4">
                <div>
                  <h4 className="font-semibold mb-2">제1조 (목적)</h4>
                  <p>본 약관은 부산항신항시설관리센터 주차장의 정기이용에 관한 조건 및 절차를 정함을 목적으로 합니다.</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">제2조 (이용기간)</h4>
                  <p>정기이용 기간은 1개월, 2개월, 3개월 중 선택하여 신청할 수 있으며, 연장을 원할 경우 재신청해야 합니다.</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">제3조 (신청절차)</h4>
                  <p>신청서 제출 후 관리자 검토를 거쳐 추첨을 통해 이용자를 선정하며, 결과는 개별 통지됩니다.</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">제4조 (이용료)</h4>
                  <p>이용료는 별도 고지되는 요금표에 따라 납부해야 하며, 기간 중 환불은 불가능합니다.</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">제5조 (준수사항)</h4>
                  <p>이용자는 주차장 이용규칙을 준수해야 하며, 위반 시 이용이 제한될 수 있습니다.</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">제6조 (책임)</h4>
                  <p>차량 도난, 파손, 사고 등에 대한 모든 책임은 이용자 본인에게 있으며, 관리센터는 책임을 지지 않습니다.</p>
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowTerms(false)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  확인
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </ClientLayout>
  );
};

export default ParkingApplicationPage; 